ApiError = require 'network/api-error'
ApiPromise = require 'network/api-promise'
Payloads = require 'network/payloads'
MemberList = undefined
queueRequire -> MemberList = require 'models/collections/member-list'
OrganizationList = undefined
queueRequire -> OrganizationList = require 'models/collections/organization-list'
PendingOrganizationList = undefined
queueRequire -> PendingOrganizationList = require 'models/collections/pending-organization-list'
PublicBoardList = undefined
queueRequire -> PublicBoardList = require 'models/collections/public-board-list'
ModelLoader = undefined
queueRequire -> ModelLoader = require 'db/model-loader'
ModelWithPreferences = require 'models/internal/model-with-preferences'
Organization  = require 'models/organization'
Board  = require 'models/board'
Promise = require 'bluebird'
_ = require 'underscore'
OrgFeatures = require 'data/org-features'
AttachmentTypes = require 'data/attachment-types'

module.exports = class Enterprise extends ModelWithPreferences
  typeName: 'Enterprise'
  nameAttr: 'name'
  urlRoot: '/1/enterprises'

  constructor: ->
    super
    @_initSubEventsOn 'organizationPrefs'
    return

  initialize: ->
    super

    @userTypesLoading = true
    @userTypesLoadFailed = false
    @licensesLoading = true
    @licensesLoadFailed = false

    @destruct [
      @organizationList = new OrganizationList()
      @memberList = new MemberList()
      @pendingOrganizationList = new PendingOrganizationList()
      @publicBoardList = new PublicBoardList()
    ]

  getOrganizationPref: (name) ->
    @get('organizationPrefs')?[name]

  setOrganizationPref: (name, value, opts, next) ->
    @update "organizationPrefs/#{ name }", value, opts, (err) =>
      unless err
        @_cascadeOrganizationPrefs(name, value)
      if _.isFunction(next)
        next(err)

  # Optimistically update all cached orgs in this enterprise
  _cascadeOrganizationPrefs: (name, value) ->
    @modelCache.all('Organization').filter (cachedOrg) =>
      cachedOrg.get('idEnterprise') == @id
    .forEach (enterpriseOrg) =>
      enterpriseOrg.set { "prefs/#{ name }": value }

  deleteOrganizationPref: (name, next) ->
    @set { "organizationPrefs/#{ name }": undefined }
    @api {
      type: 'delete'
      method: "organizationPrefs/#{ name }"
    }, next


  loadPageOfMembers: (query) ->
    ModelLoader.loadMembersOfEnterprise(@get('name'), query)
    .tap ([members]) =>
      @memberList.add(members) if members.length > 0

  loadUserTypes: ->
    return Promise.resolve() if @userTypesLoadFailed

    @userTypesLoading = true
    @trigger 'loading:userTypes'

    ModelLoader.loadEnterprise @id,
      fields: 'userTypes'
      organizations: 'none'
    # This is currently slow for big enterprises. Swallow timeouts, and you'll
    # need to render the UI as best as you can.
    .catch ApiError.Server, (err) ->
      @userTypesLoadFailed = true
      if err.message != "Gateway Timeout"
        throw err
    .then =>
      @userTypesLoading = false
      @trigger 'change:userTypes'

  loadLicenses: ->
    return Promise.resolve() if @licensesLoadFailed

    @licensesLoading = true
    @trigger 'loading:licenses'

    ModelLoader.loadEnterprise @id,
      fields: 'licenses'
      organizations: 'none'
    .catch ApiError.Server, (err) ->
      @licensesLoadFailed = true
      if err.message != "Gateway Timeout"
        throw err
    .then =>
      @licensesLoading = false
      @trigger 'change:licenses'

  loadPageOfOrganizations: (query) ->
    ModelLoader.loadEnterpriseOrganizations(@get('name'), query)
    .tap ([organizations]) =>
      @organizationList.add(organizations, { merge: true }) if organizations.length > 0

  loadMaxMembers: ->
    ModelLoader.loadEnterprise @id,
      # load prefs to refresh maxMembers
      fields: 'prefs'
      organizations: 'none'

  loadPageOfPendingOrganizations: (query) ->
    ModelLoader.loadEnterprisePendingOrganizations(@get('name'), query)
    .tap ([pendingOrganizations]) =>
      @pendingOrganizationList.add(pendingOrganizations) if pendingOrganizations.length > 0

  loadPageOfPublicBoards: (query) ->
    ModelLoader.loadEnterprisePublicBoards(@get('name'), query)
    .tap ([publicBoards]) =>
      @publicBoardList.add(publicBoards) if publicBoards.length > 0

  # This is silly, but the enterprises endpoint serves more than actual
  # enterprises. There there's also other "collections of teams" that we use to
  # configure sso and for recurring billing. This is how we tell if something
  # from that endpoint is actually an enterprise:
  isRealEnterprise: ->
    OrgFeatures.isFeatureEnabled('enterpriseUI', @get('products'))

  declinePendingOrganization: (pendingOrganization) ->
    # This is the same as org.cancelEnterpriseJoinRequest, but we can't invoke
    # that from here because the enterprise admin might not have permission to
    # access the org and therefore we might not have the org as a backbone
    # model.
    ApiPromise
      url: "/1/organizations/#{ pendingOrganization.id }/enterpriseJoinRequest"
      type: 'DELETE'
    .then =>
      @pendingOrganizationList.remove(pendingOrganization)

  acceptPendingOrganization: (pendingOrganization) ->
    ApiPromise
      url: "/1/enterprises/#{ @id }/organizations"
      type: 'PUT'
      data:
        idOrganization: pendingOrganization.id
    .then (response) =>
      @pendingOrganizationList.remove(pendingOrganization)

      teamAttrs = if _.isArray(response)
          _.find response, (team) ->
            team.id == pendingOrganization.id
        else
          response

      team = @modelCache.get('Organization', pendingOrganization.id)
      if !team?
        team = new Organization(teamAttrs, modelCache: @modelCache)
      else
        team.set(teamAttrs)

      @organizationList.add(team)
      @trigger 'accept:pendingOrganization', pendingOrganization

  updatePublicBoardsVisibility: (boards, permissionLevel) ->
    idBoards = _.pluck(boards, 'id')

    ApiPromise
      url: "/1/enterprises/#{ @id }/boards/prefs/permissionLevel"
      type: 'PUT'
      data:
        idBoards: idBoards
        value: permissionLevel
        filter: 'public'
    .then (response) =>
      @publicBoardList.remove(boards)

      # If the user tries to access the board view after
      # updating the visibility in the enterprise dashboard,
      # it will not have the new visibility so we have to
      # update the modelCache if it exists
      for idBoard in idBoards
        board = @modelCache.get('Board', idBoard)
        if board?
          updateBoardPrefs = _.extend(board.get('prefs'), permissionLevel: permissionLevel)
          boardNewPrefs = board.get('prefs')
          board.set(updateBoardPrefs)

      @trigger 'change:enterprisePublicBoards'

  getAvailableLicenses: ->
    if @get('licenses')?
      if @get('licenses')?.maxMembers
        @get('licenses').maxMembers - @get('licenses').totalMembers
      else
        Infinity

  getAttachmentRestrictions: ->
    attachmentRestrictions = @get('organizationPrefs')?.attachmentRestrictions

    if attachmentRestrictions?
      {
        enabled: attachmentRestrictions,
        disabled: _.difference(AttachmentTypes, attachmentRestrictions)
      }
    else
      { enabled: AttachmentTypes, disabled: [] }

  setMemberActive: (member, active) ->
    ApiPromise
      url: "/1/enterprises/#{ @id }/members/#{ member.id }/deactivated"
      type: 'PUT'
      data:
        fields: Payloads.enterpriseMemberFields
        value: !active
    .tap (data) =>
      @modelCache.enqueueDelta member, data
      @trigger 'change:memberActive'



// WEBPACK FOOTER //
// ./app/scripts/models/enterprise.coffee